import { For, Resource, Show, Suspense, createEffect, createSignal } from "solid-js";
import { createS3Folder } from "~/_server/s3";
import { getFileName } from "~/lib/fileUploads";
import {type S3FilesType} from "~/lib/types";

// The folder being shown belongs to the parent (it decides what to load), so this
// only ever reads props.prefix and asks the parent to change it. Keeping a private
// copy here meant clicking a folder moved the copy but not the list, so every
// folder showed its parent's contents, and new folders were made in the wrong place.
export const S3Folders = ( props: { prefix: string, rootPrefix: string, s3Objects:Resource<S3FilesType | undefined>, openFolder: ( prefix: string ) => void, goUp: () => void, refetchS3Path: () => void } ) => {
	const addFolder = async ( prefix: string, folder: string ) => {
		// A name with slashes would make nested folders, and an empty one an unnamed folder.
		const name = folder.replace( /[\\/]/g, " " ).trim();

		if ( !name ) return;

		await createS3Folder( `${prefix}${name}/` );

		props.refetchS3Path();
	}

	return (
		<div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
			<Suspense fallback={<div>Loading…</div>}>
				{/* Back is offered anywhere below the top folder of the section being shown. */}
				<Show when={props.prefix !== props.rootPrefix && props.prefix.startsWith( props.rootPrefix )}>
					<div
						class="flex flex-col items-center p-4 cursor-pointer text-primary"
						onClick={props.goUp}
					>
						<img src="/images/icon-back.svg" class="w-10" alt="Back" />
						<span>Back</span>
					</div>
				</Show>

				<Show when={props.s3Objects.latest}>
					<For each={props.s3Objects.latest?.folders}>
						{( folder ) => (
							<div
								class="flex flex-col items-center cursor-pointer text-primary p-4 border border-gray-300 rounded hover:bg-gray-50"
								onClick={() => props.openFolder( folder as string )}
							>
								<img
									src="/images/icon-folder.svg"
									class="h-8 w-8 mb-1"
									alt={`Folder: ${getFileName( folder as string )}`}
								/>
								<span class="text-xs truncate w-full text-center">
									{getFileName( folder as string )}
								</span>
							</div>
						)}
					</For>
					<Show when={props.prefix.includes( "shared" ) && props.s3Objects.latest?.folders.length == 0}>
						No folders found.
					</Show>
				</Show>

				<Show when={!props.prefix.includes( "shared" )}>
					<EmptyFolder prefix={props.prefix} addFolder={addFolder} />
				</Show>
			</Suspense>
		</div> );
}


const EmptyFolder = ( props ) => {
	let inputRef;

	const [create, setCreate] = createSignal( false );

	createEffect( () => {
		if ( create() && inputRef ) {
			inputRef.focus();
		}
	} );

	return (
		<div onClick={() => setCreate( true )} class="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-accent">
			<img src="/images/icon-plus.svg" class="w-8" alt="Add Folder" />
			<Show when={!create()}>
				<span>Add Folder</span>
			</Show>

			<Show when={create()}><input ref={( el ) => inputRef = el} onBlur={() => setCreate( false )} onChange={async e => { await props.addFolder( props.prefix, e.target.value ); setCreate( false ) }} type="text" placeholder="Enter a name for the folder name - leave empty to cancel" /></Show>
		</div>
	);
};